import React, { FC, PropsWithChildren, useState } from 'react';
import { DraggerProps } from 'antd/lib/upload';
import styled from '@emotion/styled';
import Dragger from 'antd/lib/upload/Dragger';
import { Bold42White } from '@app/components/Text';
import { pluralize } from '@app/utils/stringUtils';
import { RcFile } from 'antd/lib/upload/interface';

interface Props extends DraggerProps {
  id: string;
}

const ReactiveDragger: FC<PropsWithChildren<Props>> = ({ className, children, beforeUpload, ...draggerProps }) => {
  const [isLocalDragOver, setIsLocalDragOver] = useState(false);
  const isDragOver = isLocalDragOver;

  const handleDragEnter = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsLocalDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    const { relatedTarget } = e;
    if (!relatedTarget || !(relatedTarget instanceof Node)) {
      setIsLocalDragOver(false);
      return;
    }
    // Only set dragOver to false if we're actually leaving the component
    const currentTarget = e.currentTarget;
    if (!currentTarget.contains(relatedTarget)) {
      setIsLocalDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsLocalDragOver(false);

    if (beforeUpload && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0] as RcFile;
      const fileList = Array.from(e.dataTransfer.files).map((f) => f as RcFile);
      beforeUpload(file, fileList);
    }
  };

  return (
    <DragHoverEffectComponent
      className={className}
      isDragOver={isDragOver}
      multiple={draggerProps.multiple ?? false}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <StyledDragger {...draggerProps} beforeUpload={beforeUpload} />
      {children}
    </DragHoverEffectComponent>
  );
};

export default ReactiveDragger;

const DragHoverEffectComponent = styled.div<{ isDragOver: boolean; multiple: boolean }>`
  position: relative;

  :after {
    content: 'Drop ${(p): string => pluralize('file', p.multiple)} here';
    opacity: 0;
    ${(p): string => (p.isDragOver ? 'opacity: 0.7;' : '')}
    background-color: #838CB3;
    outline: #b0bcd0 2px dashed;
    ${Bold42White.css};
    transition: opacity 0.1s;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    pointer-events: none;
    z-index: 1;
  }
`;

const StyledDragger = styled(Dragger)<DraggerProps>`
  background: transparent !important;
  border: none !important;
  outline: none;
  text-align: start;
  padding: 0;
  cursor: default;
  position: relative;
  z-index: 2;

  .ant-upload.ant-upload-drag {
    .ant-upload {
      padding: 0;
    }

    .ant-upload-drag-container {
      display: block;
    }
  }

  .ant-upload {
    ${(p): string => (p.openFileDialogOnClick === false ? 'cursor: default' : '')};
  }
`;
